import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, CheckCircle2, Link as LinkIcon, Shield, Download } from 'lucide-react';
import { toast } from 'sonner';

interface ProofRow {
  ref_type: string;
  ref_id: string;
  event_type: string;
  event_timestamp: string;
  chain: string;
  tx_hash: string;
  sha256: string;
  verifier_url: string;
}

export default function PublicAuditPortal() {
  const { companyId } = useParams();
  const [token, setToken] = useState<string>('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ProofRow[]>([]);
  const [filter, setFilter] = useState('');
  const [verifying, setVerifying] = useState<string>('');

  async function tryOpen() {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('audit-portal-verify', {
        body: { companyId, accessCode: code || null },
      });

      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || 'Access denied');

      setToken(data.token);
      toast.success(data.public ? 'Public portal access granted' : 'Access code verified');
      // Pass the token explicitly: setToken above has not been applied to state yet.
      await loadProofs(data.token);
    } catch (e: any) {
      toast.error(e.message || 'Access denied');
    } finally {
      setLoading(false);
    }
  }

  async function loadProofs(portalToken: string) {
    try {
      // Proof listing is behind the portal token; the public_list_proofs RPC is no
      // longer executable by anon.
      const { data, error } = await supabase.functions.invoke('audit-list-proofs', {
        body: { companyId, token: portalToken },
      });

      if (error || !data?.ok) {
        console.error('Error loading proofs:', error || data?.error);
        toast.error(data?.error || 'Failed to load proofs');
        return;
      }

      setRows((data.proofs as ProofRow[]) || []);
    } catch (e: any) {
      console.error('Error loading proofs:', e);
      toast.error('Failed to load proofs');
    }
  }

  async function verifyProof(refType: string, refId: string) {
    setVerifying(refId);
    try {
      const { data, error } = await supabase.functions.invoke('audit-proof-verify', {
        body: { refType, refId },
      });

      if (error) throw error;
      toast[data?.ok ? 'success' : 'error'](
        data?.ok ? '✓ On-chain proof verified' : 'Verification failed'
      );
    } catch (e: any) {
      toast.error(e.message || 'Verification failed');
    } finally {
      setVerifying('');
    }
  }

  async function download(refType: string, refId: string) {
    if (!token) {
      toast.error('No portal token');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('audit-file-link', {
        body: { refType, refId, token },
      });

      if (error) throw error;
      if (!data?.url) {
        toast.info('No files available for download');
        return;
      }

      window.open(data.url, '_blank');
      toast.success('Opening file...');
    } catch (e: any) {
      toast.error(e.message || 'Failed to generate download link');
    }
  }

  const filteredRows = rows.filter(
    (r) =>
      !filter ||
      r.ref_id.toLowerCase().includes(filter.toLowerCase()) ||
      r.event_type.toLowerCase().includes(filter.toLowerCase()) ||
      r.ref_type.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="text-center space-y-2 mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Public Audit Portal</h1>
          </div>
          <p className="text-muted-foreground">Verify on-chain compliance proofs and audit trail</p>
        </div>

        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle>Portal Access</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2 items-center">
              <Input
                placeholder="Access code (leave empty if portal is public)"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && tryOpen()}
                disabled={!!token}
              />
              <Button onClick={tryOpen} disabled={loading || !!token}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enter'}
              </Button>
            </div>
            {!token && (
              <p className="text-xs text-muted-foreground">
                Enter the access code provided by the company to view proofs. If this company has
                enabled public mode, leave the code empty and click Enter.
              </p>
            )}
            {token && <p className="text-sm text-primary font-medium">✓ Portal access granted</p>}
          </CardContent>
        </Card>

        {!!token && (
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle>Compliance Proofs</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Filter by Ref ID, Type, or Event..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
                <Button variant="outline" onClick={() => setFilter('')}>
                  Clear
                </Button>
              </div>

              {filteredRows.length === 0 ? (
                <div className="text-center py-12 border border-dashed rounded-lg">
                  <Shield className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-sm text-muted-foreground">No proofs found</p>
                </div>
              ) : (
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Ref ID</TableHead>
                        <TableHead>Event</TableHead>
                        <TableHead>Chain</TableHead>
                        <TableHead>Transaction</TableHead>
                        <TableHead>Hash (SHA-256)</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRows.map((r) => (
                        <TableRow key={r.ref_id}>
                          <TableCell className="font-medium">{r.ref_type}</TableCell>
                          <TableCell className="font-mono text-xs">
                            {r.ref_id.slice(0, 8)}...
                          </TableCell>
                          <TableCell>{r.event_type}</TableCell>
                          <TableCell className="uppercase">{r.chain || '—'}</TableCell>
                          <TableCell>
                            {r.tx_hash ? (
                              <a
                                href={r.verifier_url || `https://basescan.org/tx/${r.tx_hash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline flex items-center gap-1 text-xs"
                              >
                                <LinkIcon className="h-3 w-3" />
                                {r.tx_hash.slice(0, 10)}...
                              </a>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs font-mono truncate max-w-[200px]">
                            {r.sha256}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => verifyProof(r.ref_type, r.ref_id)}
                                disabled={verifying === r.ref_id}
                              >
                                {verifying === r.ref_id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle2 className="h-4 w-4 mr-1" />
                                    Verify
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => download(r.ref_type, r.ref_id)}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="text-center text-xs text-muted-foreground pt-4">
          <p>
            This is a public verification portal. All proofs are cryptographically secured on-chain.
          </p>
        </div>
      </div>
    </div>
  );
}
